import {
  hasCopyFormattingLoss,
  pendingCopyHintAtom,
} from '@/components/ui/version-controller/copy-hint-state';
import { appRegistryAtom } from '@/composition/registry-atom';
import {
  USB_DICTAPHONE_SETUP_CARD_QUEUE_ITEM,
  USB_DICTAPHONE_SETUP_CARD_V2_QUEUE_ITEM,
} from '@/features/dictation/device-setup-awareness/constants';
import {
  GET_CLINICIAN_TRIAL_MODAL_EXPERIMENT_VARIANT,
  IS_DICTATION_LOGS_ENABLED,
  IS_DICTATION_LOGS_VISIBLE,
  IS_END_OF_90_DAY_TRIAL_EXPERIENCE_ENABLED,
  IS_ENTITY_VERIFICATION_ENABLED,
  IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED,
  IS_PRACTICE_INVITE_FLOW_ENABLED,
  IS_PRECHART_ENABLED,
  IS_SELF_ONBOARDING_MODAL_QUEUED,
} from '@/features/flags';
import { hcpVerificationReminderEligibleAtom } from '@/features/settings/account/components/hcp-verification/hcp-verification-atoms';
import { settingsModalAtom } from '@/features/settings/atoms/settingsModalAtom';
import { routinesSpotlightSidebarAnchorsMountedAtom } from '@/features/workflows/spotlight/routinesSpotlightAtoms';
import { isTauri } from '@/tauri/utils';
import {
  KINDE_MIGRATION_MODAL_ID,
  ModalPriority,
  type QueuedModal,
} from '@/types/modal-queue.types';
import {
  HEIDI_2_CONFIRM_ACCESS_MODAL_ID,
  HEIDI_2_EVIDENCE_TOUR_SPOTLIGHT_MODAL_ID,
} from '@heidi/agents/contracts';
import { AUTH0_APPLE_MIGRATION_STATUS_PATH } from '@heidi/auth/contracts';
import { SIDE_NAV_CREDIT_USAGE_POPOVER_MODAL_ID } from '@heidi/commerce/contracts';
import { DICTATE_REENGAGEMENT_CARD_QUEUE_ITEM } from '@heidi/dictate';
import { DICTATION_FEEDBACK_HISTORY_HREF as DICTATION_LOGS_ROUTE } from '@heidi/dictate/contracts';
import {
  dictaphoneSetupAwarenessTriggerAtom,
  dictateAddDeviceDialogRequestAtom,
  dictateReengagementTriggerAtom,
} from '@heidi/dictate/data';
import { type EligibilityContext } from '@heidi/runtime/shell';
import {
  ModalId,
  OrganisationSize,
  SignupSource,
  SubscriptionTier,
  type SubscriptionStatus,
} from '@heidi/schema/ml-scribe';
import { atom } from 'jotai';
import {
  ADAPTIVE_LEARNING_OPTIN_POPOVER_MODAL_ID,
  adaptiveLearningPopoverEligibleAtom,
} from './adaptiveLearningPopoverAtoms';
import { shouldShowAffiliateLinkModalAtom } from './affiliateLinkAtoms';
import { autoPullHistorySpotlightEligibleAtom } from './autoPullHistorySpotlightAtoms';
import { FIRMWARE_MODAL_ID, firmwareStartupSeenAtom } from './chronicle/firmware';
import { hasSeenFreeTrialModalAtom } from './freeTrialModalAtoms';
import { healthNzMigrationPromptSuppressedAtom } from './health-nz-migration-modal-atoms';
import { seenModalIdsAtom } from './modalQueueAtoms';
import {
  PATIENT_LIST_INTRODUCTION_MODAL_ID,
  PATIENT_LIST_NAV_SPOTLIGHT_MODAL_ID,
} from './patient-list-modal-ids';
import { PENDING_TEAM_INVITE_MODAL_ID } from './pending-team-invite-modal-ids';
import {
  PRECHART_BETA_INTRO_MODAL_ID,
  PRECHART_BETA_TAB_SPOTLIGHT_ID,
} from './prechart-beta-modal-ids';
import {
  SESSION_MERGE_ONBOARDING_MODAL_ID,
  SESSION_MERGE_ONBOARDING_SETTINGS_MODAL_ID,
  SESSION_MERGE_TAB_COACHMARK_MODAL_ID,
} from './session-merge-onboarding-modal-ids';
import { sessionListAnchorsMountedAtom } from './sessionListSpotlightAtoms';
import {
  scribeLayoutInviteVariantAtom,
  scribeLayoutTourEligibleAtom,
} from './sessionPageV2Atoms';
import {
  hasImportedSharedTemplateAtom,
  pendingSharedTemplateAtom,
} from './sharedTemplateAtoms';
import { sidebarPanelFeatureEnabledAtom } from './sidebarPanelAtoms';

/**
 * Writable atom for app state needed by launch modals.
 * Updated by GlobalModalsWrapper.
 *
 * Annotated with `EligibilityContext` from `@heidi/runtime/shell` (OpenSpec
 * monorepo-shell-registry task S2): package contributions receive this object
 * as their `eligible` predicate's argument, so the door's type and this atom
 * must be the same shape. The annotation is what makes that mechanical — add a
 * field here without adding it to the door (or vice versa) and this line stops
 * compiling, instead of the two silently drifting apart.
 */
export const appStateForModalsAtom = atom<EligibilityContext>({
  hasRegion: false,
  hasToken: false,
  hasOrganisationInfo: false,
  isUserPending: true,
  isUserDataLoaded: false,
  /** Managed users are excluded from non-essential product feedback prompts. */
  isManaged: false,
  pathname: '',
  hasIntegration: false,
  userTier: null as SubscriptionTier | null,
  isNewUser: false,
  // Self-onboarding modal state
  canSelfOnboardTeam: false,
  hasJoinedTeams: false,
  isUserSpecialtyEmpty: false,
  isUserFirstNameEmpty: false,
  isUserLastNameEmpty: false,
  isJoinedTeamsPending: true,
  hasPendingTeamInvitations: false,
  /**
   * Solo user or team admin, and joined-teams query is not pending/refetching.
   * Computed in GlobalModalsWrapper for launch modal gating.
   */
  canManageBilling: false,
  isKindePropertiesPending: true,
  isKindePropertiesError: false,
  isKindePropertiesErrorStale: false,
  isDesktopConnectivityOnline: true,
  userSignupSource: SignupSource.scribe,
  // Evidence launch trial and upgrade flows - for TRIAL_END_PLAN_SELECTION modal
  hasEvidenceTrialEnded: false,
  hasClinicianTrialEnded: false,
  /** Billing `previous_status_before_clinician_trial`. Used when computing `showEvidencePostUpgradeWelcomePopup` in GlobalModalsWrapper. */
  previousStatusBeforeClinicianTrial: null as SubscriptionStatus | string | null,
  /**
   * Paid Evidence org after clinician trial ended from a PRO / PRO_PLUS baseline (checkout or team upgrade).
   * Computed in GlobalModalsWrapper.
   */
  showEvidencePostUpgradeWelcomePopup: false,
  /**
   * 90-day trial end experience: show post-trial modal when the prior trial was the 90-day trial and user is not on a paid clinician tier.
   */
  showClinicianTrialEndedModal: false,
  /**
   * Trial-end usage tiering for the 90-day trial end experience (low vs high usage).
   * `true` means the user (or, for team admins, their team in aggregate) had `< 5`
   * `answers_from_evidence` during the trial — i.e. they should see the soft-touch
   * discreet card first instead of the full upgrade modal. Computed in
   * `GlobalModalsWrapper` via `useTrialRecapUsageStats`.
   */
  isTrialEndLowUsage: false,
  /**
   * `false` while evidence usage stats (or team details needed for team stats) are
   * still loading. The launch queue must not enqueue discreet or big trial-end
   * modals until this is `true`, or low-usage users briefly get the big modal.
   */
  isTrialEndUsageStatsReady: false,
  /**
   * `true` when the user is a non-admin member of a team (`isNonAdminTeamMember` from
   * `useCombinedQueriesTeamGeneralInfo`). Used in `launchModalsConfigAtom` to gate
   * the post-trial-end big modal and to suppress the discreet card entirely for
   * non-admin + low-usage users.
   */
  isNonAdminTeamMember: false,
  /** True when trial-end plan selection should show: evidence trial ended (and not user-cancelled) or clinician trial ended */
  showEndTrialModal: false,
  // Trial ending soon (1-3 days) - for upgrade modals on launch
  evidenceTrialDaysRemaining: 0,
  clinicianTrialDaysLeft: 0,
  isInEvidenceTrial: false,
  isInScribeClinicianTrial: false,
  isNewUserAfterEvidenceLaunch: false,
  isTeamAdmin: false,
  /** When true, user has (or has ever had) evidence billing; do not show clinician/evidence trial modals */
  hasEvidenceBillingHistory: false,
  isClinicianPracticeUser: false, // tier == CLINICIAN_PRO_PLUS
  isClinicianUser: false, // tier == CLINICIAN_PRO
  /** When true, onboarding modal onSubmit is in progress; do not show evidence/clinician/new practice trial modals until it completes. */
  isOnboardingSubmitInProgress: false,
  clinicianTrialAvailable: false,
  /**
   * Onboarding-declared org size (`organisation_info.size`). `null` when
   * unset/skipped. CLINICIAN_TRIAL: JUST_ME / unset / flag off; PRACTICE_TRIAL:
   * pendingPracticeTrialModal.
   */
  organisationSize: null as OrganisationSize | null,
  /**
   * sessionStorage flag at onboarding submit (team org size + practice-invite
   * flag). Gates PRACTICE_TRIAL so existing users (empty seen_modals for new
   * ModalId) never see it.
   */
  pendingPracticeTrialModal: false,
  /** True when the user is on a fake (Stripe-less) clinician/practice reverse trial. */
  isFakeClinicianTrial: false,
  // Menu coachmark tour state
  isMobile: false,
  /** True if the current route has sidebar content (matches ROUTE_TO_SIDEBAR_CONTENT) */
  hasSidebarRoute: false,
  /** Flagsmith gate for the share-session announce spotlight */
  isShareSessionAnnounceSpotlightEnabled: false,
  /** Flagsmith gate for the auto-link-sessions slice (Auto Pull History spotlight + auto-include hook) */
  isAutoLinkSessionsEnabled: false,
  /** True when the user's org permits external session link sharing. */
  isLinkShareAvailable: false,
  /** True when user has active Evidence access tier (from useEvidenceAccess). */
  hasEvidenceAccess: false,
  /** True when the user account is at least 7 days old. Gates the web/desktop dictation onboarding modals. */
  isUserAtLeastAWeekOld: false,
  /** True once an eligible refreshed-layout user reaches the seven-day CSAT delay. */
  showScribeLayoutCsatPopover: false,
  /** True when running in the Tauri desktop app (`isTauri()`). Used to gate web vs desktop dictation onboarding modals. */
  isTauri: false,
  /** PostHog gate for the desktop dictation flag (key: `release-desktop-dictation`, independent of `isTauri()`). */
  isDesktopDictationFlagEnabled: false,
  /** PostHog gate for the web dictation flag (key: `universal-dictation`, raw flag value, independent of user preference). */
  isWebDictationFlagEnabled: false,
  /** Flagsmith gate for the web dictation onboarding modal (`release-web-dictation-onboarding`). */
  isWebDictationOnboardingFlagEnabled: false,
  /** Flagsmith gate for the general dictate re-engagement card (`release-scribe-dictate-reengagement-general-card`). */
  isDictateReengagementCardFlagEnabled: false,
  /** Flagsmith gate for the phase-1 detected-device awareness popup (`release-scribe-dictate-device-detected-popup`). */
  isDictateDeviceDetectedPopupFlagEnabled: false,
  /** Flagsmith gate for the phase-2 device setup flow (`release-scribe-dictate-device-setup`). Picks the awareness card's phase-2 modal id. */
  isDictateDeviceSetupFlagEnabled: false,
  /** True when dictation is fully enabled for the user (platform flag + user preference, not Shadow Heidi). Gates the dictation premium trial modal. */
  isDictationEnabled: false,
  /** True when the Chronicle device reports a firmware update is available. */
  firmwareUpdateAvailable: false,
  /** Current OTA status string, or null when no update is in progress. */
  firmwareUpdateStatus: null as string | null,
  /** True when a session recording is active. */
  isRecording: false,
  /**
   * The in-session Ask Heidi chat (`evidenceSidebarOpenAtom`) is open. Ask
   * Heidi carries its own "How did Heidi do?" prompt, so feedback popovers
   * that would stack on it read this. Published by GlobalModalsWrapper.
   */
  isAskHeidiOpen: false,
  /** Flagsmith gate for the Chronicle firmware startup check. */
  isFirmwareStartupCheckEnabled: false,
  /** True when the user's team is Stripe-managed (`is_stripe_managed`). Solo users skip this gate. */
  isTeamStripeManaged: false,
  /**
   * Flagsmith gate (`release-scribe-enable-patient-list`) for the Patients GA
   * introduction modal, AND-ed with "not withheld by an EHR launch". See
   * `EligibilityContext`.
   */
  canSurfacePatientListNudges: false,
  /**
   * The platform-split session-merge flag (`release-apps-session-merge-web` on
   * web, `release-apps-session-merge-desktop` on desktop) is on but auto
   * session merge is still off —
   * i.e. the one-time "Enable Session Merge" onboarding popover may show.
   * Computed in GlobalModalsWrapper via `useCanPromptSessionMergeOnboarding`.
   */
  canPromptSessionMergeOnboarding: false,
  /**
   * The open session renders a Session Merge tab (merge enabled + non-empty
   * linked group). Gates the tab coachmark's enqueue so it can't sit invisibly
   * at the head of the LOW-priority queue (blocking sibling spotlights) on
   * tab-less screens. Computed in GlobalModalsWrapper via `useOpenSessionHasMergeTab`.
   */
  hasSessionMergeTab: false,
  /**
   * A clinical recording is active on the open session, **including paused**
   * recordings and Chronicle device captures. Unlike `isRecording` (running
   * timer only — pausing sets it false mid-consult), this is the signal for
   * "don't interrupt a clinical session" gates (modal priority stack R1).
   * Computed in GlobalModalsWrapper via `useIsClinicalRecordingActive`.
   */
  isClinicalRecordingActive: false,
  /**
   * True for managed / enterprise accounts (`user.is_managed`). T3–T5 launch
   * surfaces must be suppressed for these users (modal priority stack R2).
   */
  isUserManaged: false,
  /**
   * True when the user account is at least 14 days old. Gates the Patients GA
   * introduction modal so brand-new signups meet Patients organically rather
   * than getting a feature-intro modal on day one (mirrors `isUserAtLeastAWeekOld`).
   */
  isUserAtLeastTwoWeeksOld: false,
  /**
   * True when the Patients nav spotlight should show: the introduction modal was
   * seen 2+ days ago (immediately under the local force-flag) and the feature is
   * on. Computed in GlobalModalsWrapper from the intro modal's `seen_modals` entry.
   */
  showPatientListNavSpotlight: false,
  /**
   * True when the session list spotlight tour should show: the new session list
   * flag (release-scribe-enable-new-session-list) is on and the account is at
   * least 3 days old. Computed in GlobalModalsWrapper.
   */
  showSessionListSpotlight: false,
  /**
   * True when the Routines onboarding spotlight should show: Heidi Work /
   * workflows is on. Desktop, Work path, sidebar anchors, and recording
   * guards live in `launchModalsAtom`. Computed in GlobalModalsWrapper.
   */
  showRoutinesSpotlight: false,
  /**
   * True when the Heidi II Evidence tour should show: onboarding flag on, on
   * Heidi II, past the welcome, tour not yet closed, and on any page with the
   * rail (or heading Home) for step 0 / on the Work home tree for later steps. Computed in
   * GlobalModalsWrapper; device and recording guards live here.
   */
  showHeidi2EvidenceTourSpotlight: false,
  /**
   * Team admin on Heidi II with `release-heidi-2-team-access-controls` on.
   * Gates the "Your team can start using Heidi II" confirm-access modal.
   * Computed in GlobalModalsWrapper; device and recording guards live here.
   */
  showHeidi2ConfirmAccessModal: false,
  /**
   * Desktop dictate-origin users on `/dictation-logs` with >20 dictations.
   * Gates the Scribe cross-sell spotlight. Computed in GlobalModalsWrapper.
   * Route-scoped: leaving the logs page drops it from the launch queue.
   */
  showScribeCrossSellSpotlight: false,
  /** True when the Kinde user should be migrated to Auth0 (forced modal). */
  isEligibleForKindeMigrationModal: false,
  isHealthNzMigrationPromptEnabled: false,
  isHeidi2OptInEnabled: false,
  shouldQueueSideNavCreditUsageThresholdPopover: false,
});

/**
 * Exported for the suite that pins it. The id is commerce's — the popover it
 * launches lives behind that door — but the queue row is this file's, typed by
 * the app's own `QueuedModal`.
 */
export const SIDE_NAV_CREDIT_USAGE_POPOVER_QUEUE_ITEM: QueuedModal = {
  id: SIDE_NAV_CREDIT_USAGE_POPOVER_MODAL_ID,
  priority: ModalPriority.HIGH,
  dismissible: true,
  showOnce: false,
};

/**
 * The dictaphone awareness card id for the current phase — the ONE source the
 * launch row enqueues and the host's `useQueuedModal` listens on. Both read the
 * same atom in the same store, so a mid-session flag flip cannot leave the
 * queue head on an id no mounted component dequeues.
 */
export const usbDictaphoneSetupCardQueueItemAtom = atom((get) =>
  get(appStateForModalsAtom).isDictateDeviceSetupFlagEnabled
    ? USB_DICTAPHONE_SETUP_CARD_V2_QUEUE_ITEM
    : USB_DICTAPHONE_SETUP_CARD_QUEUE_ITEM
);

export const launchModalsAtom = atom<QueuedModal[]>((get) => {
  const appState = get(appStateForModalsAtom);
  const hasSeenFreeTrialModal = get(hasSeenFreeTrialModalAtom);
  const shouldShowAffiliateLinkModal = get(shouldShowAffiliateLinkModalAtom);
  const hasImportedSharedTemplate = get(hasImportedSharedTemplateAtom);
  const pendingSharedTemplate = get(pendingSharedTemplateAtom);

  const isHealthNzMigrationPromptSuppressed = get(healthNzMigrationPromptSuppressedAtom);

  const seenModalIds = get(seenModalIdsAtom);
  const isAutoPullHistorySpotlightEligible = get(autoPullHistorySpotlightEligibleAtom);
  const isSessionListAnchorsMounted = get(sessionListAnchorsMountedAtom);
  const isRoutinesSpotlightSidebarAnchorsMounted = get(
    routinesSpotlightSidebarAnchorsMountedAtom
  );
  const firmwareStartupSeen = get(firmwareStartupSeenAtom);
  // Settings opens as a modal overlay (doesn't change router.pathname), so the
  // "settings open on Defaults" check reads the settings-modal atom. The
  // pathname fallback covers the non-modal (full-page route) settings build.
  const settingsModal = get(settingsModalAtom);

  const isLaunchQueueBlocked =
    !appState.isUserDataLoaded ||
    seenModalIds === null ||
    appState.isUserPending ||
    appState.isKindePropertiesPending;

  const modals: QueuedModal[] = [];

  const addModalToQueue = (modal: QueuedModal) => {
    // Only check seenModalIds if modal should only be shown once
    if (modal.showOnce && seenModalIds !== null && seenModalIds.has(modal.id)) {
      return;
    }
    if (modal.showOnlyAfterAccountCreation && !appState.isNewUser) {
      return;
    }
    modals.push(modal);
  };

  // Legacy inline parity: !region && kinde error && token && desktop online.
  //
  // The staleness term is the one addition to that parity, and it is what makes
  // the condition read forwards instead of backwards. A kinde-properties failure
  // survives the offline window that produced it, so on reconnect the first
  // three terms are all still true and the connectivity term flips back on —
  // opening this non-dismissible modal over an operation that has usually
  // already succeeded. `isKindePropertiesErrorStale` ages the error against the
  // current online episode (see `useKindePropertiesReconnectRecovery`, which
  // also drives the recovery refetch that clears it): a failure is fresh — and
  // so a real firewall block shows — for as long as connectivity holds steady
  // at `online`. Any later connectivity change re-ages it, including one that
  // returns to `online`, because the evidence is now about a connection that no
  // longer exists; the refetch that same transition fires is what re-proves it.
  if (
    !appState.hasRegion &&
    appState.isKindePropertiesError &&
    appState.hasToken &&
    appState.isDesktopConnectivityOnline &&
    !appState.isKindePropertiesErrorStale
  ) {
    addModalToQueue({
      id: ModalId.T1_NETWORK_FIREWALL_ERROR_MODAL,
      priority: ModalPriority.HIGHEST,
      dismissible: false,
      showOnce: false,
    });
  }

  // T1 network error skips user-fetch / seen_modals gate (legacy inline parity).
  if (isLaunchQueueBlocked) {
    return modals;
  }

  // Copy feedback is attempt-scoped and owned by a visible toolbar, so it only
  // exists after a copy. It sits above the launch-only route guard on purpose;
  // the toolbar's anchor-visibility check is its route gate.
  const copyHint = get(pendingCopyHintAtom);
  if (hasCopyFormattingLoss(copyHint?.formattingLoss)) {
    addModalToQueue({
      id: ModalId.ACTION_COPY_FORMATTING_LOSS_POPOVER,
      priority: ModalPriority.NORMAL,
      dismissible: true,
      showOnce: false,
    });
  } else if (
    copyHint?.discovery &&
    !appState.isClinicalRecordingActive &&
    !appState.isUserManaged
  ) {
    // Copy's own rules hold whatever the flags. R5 isn't applied here:
    // `currentQueuedModalAtom` filters this row, and the toolbar
    // (`useCopyHints`, in the UI PR) checks the same predicate before arming.
    addModalToQueue({
      id: ModalId.ACTION_COPY_SETTINGS_COACHMARK,
      priority: ModalPriority.LOW,
      dismissible: true,
      // The impression is persisted on display; keep that same surface open.
      showOnce: !copyHint.displayed,
    });
  }

  const isValidPath =
    appState.pathname !== '/scribe/new-user' &&
    appState.pathname !== '/new-team-user' &&
    appState.pathname !== '/organization-access-required' &&
    appState.pathname !== '/account-suspended' &&
    appState.pathname !== '/dictate-onboarding' &&
    // The Apple migration status page is the transient landing the migration
    // flow returns to — no forced launch modal (least of all the migration
    // modal itself) should pop on top of the success/failure surface.
    appState.pathname !== AUTH0_APPLE_MIGRATION_STATUS_PATH;

  // Forced Kinde → Auth0 migration modal. CRITICAL priority, non-dismissible,
  // and re-shown until migration completes (showOnce: false, so it never
  // consults the seen set). Eligibility is computed in GlobalModalsWrapper.
  // Gated on isValidPath like the other forced launch modals so a
  // non-dismissible modal can't hard-block an eligible Kinde user on
  // recovery/onboarding routes (e.g. /account-suspended,
  // /organization-access-required, /scribe/new-user).
  if (appState.isEligibleForKindeMigrationModal && appState.hasToken && isValidPath) {
    addModalToQueue({
      id: KINDE_MIGRATION_MODAL_ID,
      priority: ModalPriority.CRITICAL,
      dismissible: false,
      showOnce: false,
    });
  }

  // T2 compliance notice: CRITICAL so it isn't buried, dismissible because it's a
  // notice rather than a gate, and withheld during an active clinical recording.
  // The flag segment is the ONLY audience gate — nothing here or downstream
  // excludes managed users, so widening the flag ships the notice to them too.
  if (
    appState.isHealthNzMigrationPromptEnabled &&
    !isHealthNzMigrationPromptSuppressed &&
    appState.hasToken &&
    isValidPath &&
    !appState.isClinicalRecordingActive
  ) {
    addModalToQueue({
      id: ModalId.CUSTOM_AUDIENCE_HEALTH_NZ_MIGRATION_MODAL,
      priority: ModalPriority.CRITICAL,
      dismissible: true,
      showOnce: true,
    });
  }

  const isPracticeInviteFlowEnabled = IS_PRACTICE_INVITE_FLOW_ENABLED();

  // Users already on a team answer invites from the workspace switcher, not a forced modal.
  // SSO self-onboarders never see it: joining through self-onboarding accepts the invite.
  const shouldShowPendingTeamInvite =
    appState.hasPendingTeamInvitations &&
    !appState.hasJoinedTeams &&
    !appState.canSelfOnboardTeam &&
    isValidPath &&
    appState.pathname !== '/join-team' &&
    appState.hasToken &&
    !appState.isKindePropertiesPending &&
    !appState.isKindePropertiesError &&
    !appState.isJoinedTeamsPending;

  if (shouldShowPendingTeamInvite) {
    addModalToQueue({
      id: PENDING_TEAM_INVITE_MODAL_ID,
      priority: ModalPriority.HIGHEST,
      dismissible: false,
      showOnce: false,
    });
  }

  // User self-onboarding modal - highest priority (only when queued flag is enabled)
  const shouldShowUserSelfOnboarding =
    IS_SELF_ONBOARDING_MODAL_QUEUED() &&
    appState.canSelfOnboardTeam &&
    (!appState.hasJoinedTeams ||
      appState.isUserSpecialtyEmpty ||
      appState.isUserFirstNameEmpty ||
      appState.isUserLastNameEmpty) &&
    isValidPath &&
    appState.pathname !== '/join-team' &&
    appState.hasToken &&
    !appState.isKindePropertiesPending &&
    !appState.isKindePropertiesError &&
    !appState.isJoinedTeamsPending;

  if (shouldShowUserSelfOnboarding) {
    addModalToQueue({
      id: ModalId.USER_SELF_ONBOARDING,
      priority: ModalPriority.HIGHEST,
      dismissible: false,
    });
  }

  const isWidgetAuthPath = appState.pathname.includes('/integration/widget/auth');
  const shouldShowRequireTeamMemberSpecialty =
    appState.hasJoinedTeams &&
    appState.isUserSpecialtyEmpty &&
    !appState.canSelfOnboardTeam &&
    isValidPath &&
    appState.pathname !== '/join-team' &&
    !isWidgetAuthPath &&
    appState.hasToken &&
    !appState.isKindePropertiesPending &&
    !appState.isKindePropertiesError &&
    !appState.isJoinedTeamsPending &&
    appState.isDesktopConnectivityOnline;

  if (shouldShowRequireTeamMemberSpecialty) {
    addModalToQueue({
      id: ModalId.T0_TEAM_MEMBER_REQUIRE_SPECIALTY_MODAL,
      priority: ModalPriority.HIGHEST,
      dismissible: false,
      showOnce: false,
    });
  }

  const integrationAndNeedsUpgrade =
    appState.hasIntegration &&
    !!appState.userTier &&
    appState.userTier !== SubscriptionTier.PRO_PLUS &&
    appState.userTier !== SubscriptionTier.CLINICIAN_PRO_PLUS;

  const hasPendingSharedTemplateImport = !!pendingSharedTemplate;
  const shouldShowFreeTrial =
    appState.hasRegion &&
    appState.hasToken &&
    appState.hasOrganisationInfo &&
    !integrationAndNeedsUpgrade &&
    isValidPath &&
    !hasSeenFreeTrialModal &&
    !hasPendingSharedTemplateImport &&
    !!appState.userTier &&
    appState.userTier !== SubscriptionTier.ENTERPRISE &&
    appState.userTier !== SubscriptionTier.PRO_PLUS &&
    (appState.userTier === SubscriptionTier.PRO || !isPracticeInviteFlowEnabled) &&
    // show new clinician trial modals instead if evidence launch flag enabled and user is not signing up for scribe plus
    (!IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED() ||
      appState.userSignupSource === SignupSource.scribe_plus);

  if (shouldShowFreeTrial) {
    addModalToQueue({
      id: ModalId.FREE_TRIAL,
      priority: ModalPriority.HIGH,
      dismissible: true,
      showOnlyAfterAccountCreation: !isPracticeInviteFlowEnabled,
      showOnce: true,
    });
  }

  const shouldShowAffiliateLink =
    shouldShowAffiliateLinkModal &&
    appState.hasRegion &&
    appState.hasToken &&
    appState.hasOrganisationInfo &&
    appState.pathname !== '/scribe/new-user' &&
    appState.pathname !== '/new-team-user' &&
    appState.pathname !== '/onboarding' &&
    !appState.isUserPending &&
    isValidPath;

  if (shouldShowAffiliateLink) {
    addModalToQueue({
      id: ModalId.AFFILIATE_LINK,
      priority: ModalPriority.HIGH,
      dismissible: true,
      showOnlyAfterAccountCreation: false,
    });
  }

  const shouldShowCustomTemplateOnboarding =
    hasImportedSharedTemplate &&
    appState.hasRegion &&
    appState.hasToken &&
    appState.hasOrganisationInfo &&
    !appState.isUserPending &&
    isValidPath;

  if (shouldShowCustomTemplateOnboarding) {
    addModalToQueue({
      id: ModalId.CUSTOM_TEMPLATE_ONBOARDING,
      priority: ModalPriority.NORMAL,
      dismissible: true,
      showOnlyAfterAccountCreation: true,
      showOnce: true,
    });
  }

  const shouldShowEvidenceProTrialModal =
    appState.hasToken &&
    appState.userSignupSource === SignupSource.evidence &&
    appState.hasOrganisationInfo &&
    appState.pathname.includes('/scribe/chat') &&
    IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED() &&
    !appState.hasEvidenceBillingHistory &&
    !appState.isOnboardingSubmitInProgress;

  if (shouldShowEvidenceProTrialModal) {
    // Evidence Plus trial modal deleted (GRO-3609) — always Clinician trial.
    // Still needs clinicianTrialAvailable — ClinicianTrialModal has no internal gate.
    if (appState.clinicianTrialAvailable) {
      addModalToQueue({
        id: ModalId.CLINICIAN_TRIAL,
        priority: ModalPriority.CRITICAL,
        dismissible: false,
        showOnlyAfterAccountCreation: false,
        showOnce: true,
      });
    }
  }

  // Shared path/signup gates for onboarding trial start modals.
  // PRACTICE_TRIAL and CLINICIAN_TRIAL branch separately below.
  const isEligibleForOnboardingTrialModal =
    IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED() &&
    appState.isNewUserAfterEvidenceLaunch &&
    appState.userSignupSource === SignupSource.scribe &&
    appState.hasToken &&
    appState.hasOrganisationInfo &&
    isValidPath &&
    appState.pathname !== '/dictation-logs' &&
    !appState.isOnboardingSubmitInProgress;

  // PRACTICE_TRIAL — sessionStorage at onboarding submit when team org size +
  // practice-invite flag (same cohort as showsInviteTeam). BE already activated
  // Practice on org-size PATCH, so modal is informational — must NOT gate on
  // clinicianTrialAvailable / evidence billing history. pendingPracticeTrialModal
  // keeps existing users (new ModalId, empty seen_modals) from seeing it next login.
  const shouldShowPracticeTrialModal =
    isEligibleForOnboardingTrialModal && appState.pendingPracticeTrialModal;

  if (shouldShowPracticeTrialModal) {
    addModalToQueue({
      id: ModalId.PRACTICE_TRIAL,
      priority: ModalPriority.CRITICAL,
      dismissible: false,
      showOnlyAfterAccountCreation: false,
      showOnce: true,
    });
  }

  // CLINICIAN_TRIAL — solo / no pending practice modal. Needs
  // clinicianTrialAvailable + no evidence billing history + experiment.
  const isTeamOrgSize =
    appState.organisationSize != null &&
    appState.organisationSize !== OrganisationSize.JUST_ME;
  const shouldShowClinicianTrialModal =
    isEligibleForOnboardingTrialModal &&
    !shouldShowPracticeTrialModal &&
    !(isTeamOrgSize && isPracticeInviteFlowEnabled) &&
    !appState.hasEvidenceBillingHistory &&
    appState.clinicianTrialAvailable &&
    GET_CLINICIAN_TRIAL_MODAL_EXPERIMENT_VARIANT() !== 'NONE';

  if (shouldShowClinicianTrialModal) {
    addModalToQueue({
      id: ModalId.CLINICIAN_TRIAL,
      priority: ModalPriority.CRITICAL,
      dismissible: false,
      showOnlyAfterAccountCreation: false,
      showOnce: true,
    });
  }

  // Post–trial-end matrix (low vs high usage × admin vs non-admin):
  //
  //   admin     + low usage  → discreet card (then big modal on "Learn more")
  //   admin     + high usage → big modal directly
  //   non-admin + low usage  → NOTHING (tightens the existing gate — they used
  //                            to fall through into the broken admin branch)
  //   non-admin + high usage → big modal directly (request-to-upgrade branch
  //                            handled inside the modal itself)
  //
  // The discreet popup is enqueued at launch, but the big modal that it leads
  // to is enqueued user-triggered from `useTrialEndDiscreetCard.handleLearnMore`
  // — never auto-queued for low-usage admins.
  // Non-admin team members legitimately don't have `organisation_info`
  // populated on their user doc — the team admin owns the org. Requiring
  // their personal org info here would silently exclude the entire
  // "Practice non-admin + high usage" branch of the matrix in production,
  // so we accept either signal: the user has their own org, OR they're
  // flagged as a non-admin team member (their team has the org info).
  const eligibleForClinicianTrialEnd =
    IS_END_OF_90_DAY_TRIAL_EXPERIENCE_ENABLED() &&
    isValidPath &&
    appState.hasToken &&
    (appState.hasOrganisationInfo || appState.isNonAdminTeamMember) &&
    appState.showClinicianTrialEndedModal;

  const shouldShowClinicianTrialEndDiscreet =
    eligibleForClinicianTrialEnd &&
    appState.isTrialEndUsageStatsReady &&
    appState.canManageBilling &&
    appState.isTrialEndLowUsage;

  if (shouldShowClinicianTrialEndDiscreet) {
    addModalToQueue({
      id: ModalId.NINETY_DAY_TRIAL_END_DISCREET_POPUP,
      priority: ModalPriority.NORMAL,
      dismissible: true,
      showOnlyAfterAccountCreation: false,
      showOnce: true,
    });
  }

  const shouldShowClinicianTrialEndedModal =
    eligibleForClinicianTrialEnd &&
    appState.isTrialEndUsageStatsReady &&
    !appState.isTrialEndLowUsage &&
    (appState.canManageBilling || appState.isNonAdminTeamMember);

  if (shouldShowClinicianTrialEndedModal) {
    addModalToQueue({
      id: ModalId.NINETY_DAY_TRIAL_END_MODAL,
      priority: ModalPriority.NORMAL,
      dismissible: false,
      showOnlyAfterAccountCreation: false,
      showOnce: true,
    });
  }

  const shouldShowTrialEndPlanSelection =
    IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED() &&
    appState.hasToken &&
    appState.hasOrganisationInfo &&
    isValidPath &&
    appState.showEndTrialModal &&
    appState.canManageBilling &&
    // Suppress legacy plan-selection for the entire 90-day trial end experience,
    // including the low-usage discreet path (where the big modal is enqueued
    // user-triggered later, not at launch).
    !eligibleForClinicianTrialEnd;

  if (shouldShowTrialEndPlanSelection) {
    addModalToQueue({
      id: ModalId.TRIAL_END_PLAN_SELECTION,
      priority: ModalPriority.NORMAL,
      dismissible: false,
      showOnlyAfterAccountCreation: false,
      showOnce: true,
    });
  }

  const is90DayTrialPopupEnabled =
    IS_END_OF_90_DAY_TRIAL_EXPERIENCE_ENABLED() && appState.isFakeClinicianTrial;

  // Trial ending soon (1-3 days remaining): show upgrade modal on launch based on signup source
  const evidenceTrialEndingSoon =
    IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED() &&
    appState.userSignupSource === SignupSource.evidence &&
    appState.hasToken &&
    appState.hasOrganisationInfo &&
    isValidPath &&
    !appState.hasEvidenceTrialEnded &&
    appState.isInEvidenceTrial &&
    appState.evidenceTrialDaysRemaining <= 3 &&
    appState.canManageBilling;

  const clinicianTrialEndingSoon =
    IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED() &&
    appState.userSignupSource === SignupSource.scribe &&
    appState.hasToken &&
    appState.hasOrganisationInfo &&
    isValidPath &&
    appState.isInScribeClinicianTrial &&
    !appState.hasClinicianTrialEnded &&
    appState.clinicianTrialDaysLeft <= 3 &&
    appState.canManageBilling;

  if (
    !is90DayTrialPopupEnabled &&
    (evidenceTrialEndingSoon || clinicianTrialEndingSoon)
  ) {
    addModalToQueue({
      id: ModalId.TRIAL_ENDING_SOON_REMINDER,
      priority: ModalPriority.NORMAL,
      dismissible: true,
      showOnlyAfterAccountCreation: false,
      showOnce: true,
    });
  }

  const shouldShow90DayTrialReminderPopup =
    is90DayTrialPopupEnabled &&
    appState.hasToken &&
    appState.hasOrganisationInfo &&
    isValidPath &&
    appState.clinicianTrialDaysLeft <= 7;

  if (shouldShow90DayTrialReminderPopup) {
    addModalToQueue({
      id: ModalId.NINETY_DAY_TRIAL_ENDING_SOON_REMINDER_POPUP,
      priority: ModalPriority.LOW,
      dismissible: true,
      showOnlyAfterAccountCreation: false,
      showOnce: true,
    });
  }

  const shouldShowEvidencePostUpgradeWelcomePopup =
    IS_END_OF_90_DAY_TRIAL_EXPERIENCE_ENABLED() &&
    appState.hasToken &&
    appState.hasOrganisationInfo &&
    isValidPath &&
    appState.showEvidencePostUpgradeWelcomePopup;

  if (shouldShowEvidencePostUpgradeWelcomePopup) {
    addModalToQueue({
      id: ModalId.EVIDENCE_POST_UPGRADE_WELCOME_POPUP,
      priority: ModalPriority.HIGH,
      dismissible: true,
      showOnlyAfterAccountCreation: false,
      showOnce: true,
    });
  }

  // Weekly clinician (NPI) verification reminder. T2 compliance; HIGH leads
  // the nudges but stays under self-onboarding / trial paywalls. The badge
  // probe supplies eligibility (`unverified` / `rejected`), so a verify or a
  // parked review drops the row. `showOnce` + the 7-day rule in
  // `modalClosedRules` give the weekly cadence. Not shown over a clinical
  // recording (R1), on onboarding / join-team / widget-auth, or with the
  // settings modal open (the account card carries the same CTA). Managed
  // users are not excluded.
  // Debugger: __heidiModals.forceShow('CUSTOM_AUDIENCE_HCP_VERIFICATION_REMINDER_MODAL')
  const isHcpVerificationReminderEligible = get(hcpVerificationReminderEligibleAtom);
  if (
    isHcpVerificationReminderEligible &&
    appState.hasToken &&
    isValidPath &&
    appState.pathname !== '/onboarding' &&
    appState.pathname !== '/join-team' &&
    !isWidgetAuthPath &&
    !settingsModal.open &&
    !appState.isClinicalRecordingActive
  ) {
    addModalToQueue({
      id: ModalId.CUSTOM_AUDIENCE_HCP_VERIFICATION_REMINDER_MODAL,
      priority: ModalPriority.HIGH,
      dismissible: true,
      showOnce: true,
    });
  }

  const shouldShowScribeOnboardingModal =
    appState.hasToken &&
    appState.userSignupSource === SignupSource.evidence &&
    appState.hasOrganisationInfo &&
    appState.pathname.includes('/scribe/session');

  if (shouldShowScribeOnboardingModal) {
    addModalToQueue({
      id: ModalId.SCRIBE_ONBOARDING,
      priority: ModalPriority.NORMAL,
      dismissible: true,
      showOnlyAfterAccountCreation: false,
      showOnce: true,
    });
  }

  const shouldShowWebDictationOnboardingModal =
    appState.isWebDictationFlagEnabled &&
    appState.isDesktopDictationFlagEnabled &&
    appState.isWebDictationOnboardingFlagEnabled &&
    !isTauri() &&
    appState.userSignupSource !== SignupSource.dictate &&
    appState.hasToken &&
    appState.hasOrganisationInfo &&
    !appState.isMobile &&
    appState.isUserAtLeastAWeekOld &&
    isValidPath;

  if (shouldShowWebDictationOnboardingModal) {
    addModalToQueue({
      id: ModalId.UNIVERSAL_DICTATION_ONBOARDING_MODAL,
      priority: ModalPriority.NORMAL,
      dismissible: true,
      showOnce: true,
    });
  }

  // USB dictaphone awareness card — armed by the detected-device trigger
  // hook when a supported dictaphone connects after the boot snapshot (phase
  // 1), or for a device already attached at launch (phase 2). Queued
  // BEFORE the re-engagement card so an equal-priority collision resolves to
  // the connect moment (more time-critical). Never during a clinical session:
  // armed-but-recording defers here, and the card appears when the consult ends.
  // Deliberately no `isUserManaged` gate — hardware connect-time awareness is
  // product functionality, not a non-essential prompt. Either device phase
  // flag AND the platform-dictation gate are checked here (mirroring the
  // trigger hook's composed visibility and the re-engagement row below), so
  // the kill switches hold even when the host tree unmounts and the trigger
  // hook's retraction cannot run. Phase 2 picks the setup card id.
  const dictaphoneSetupDeviceKey = get(dictaphoneSetupAwarenessTriggerAtom);
  const settingsRoute = settingsModal.open ? settingsModal.route : appState.pathname;
  const onDictaphoneSettings =
    settingsRoute === '/settings/dictate' || settingsRoute === '/settings/defaults';
  const inDeviceSetupDialog = get(dictateAddDeviceDialogRequestAtom) !== null;
  const shouldShowUsbDictaphoneSetupCard =
    isTauri() &&
    (appState.isDictateDeviceDetectedPopupFlagEnabled ||
      appState.isDictateDeviceSetupFlagEnabled) &&
    appState.isDesktopDictationFlagEnabled &&
    dictaphoneSetupDeviceKey !== null &&
    appState.hasToken &&
    isValidPath &&
    !appState.isClinicalRecordingActive &&
    !onDictaphoneSettings &&
    !inDeviceSetupDialog;

  if (shouldShowUsbDictaphoneSetupCard) {
    addModalToQueue(get(usbDictaphoneSetupCardQueueItemAtom));
  }

  const dictateReengagementTrigger = get(dictateReengagementTriggerAtom);
  // Inline during a paused consult (`isRecording` false, clinical still active)
  // is the natural moment. Hide the card over a live consult. The recording
  // trigger waits until the consult is idle (not paused).
  const shouldShowDictateReengagementCard =
    isTauri() &&
    appState.isDesktopDictationFlagEnabled &&
    appState.isDictateReengagementCardFlagEnabled &&
    !appState.isUserManaged &&
    appState.hasToken &&
    isValidPath &&
    dictateReengagementTrigger !== null &&
    ((dictateReengagementTrigger === 'inline' && !appState.isRecording) ||
      !appState.isClinicalRecordingActive);

  if (shouldShowDictateReengagementCard) {
    addModalToQueue(DICTATE_REENGAGEMENT_CARD_QUEUE_ITEM);
  }
  // Menu coachmark tour - LOW priority, waits for other modals to complete
  // Welcome spotlight shows on any page; "Show me" navigates to a sidebar route for the tour steps
  // Reads the reactive atom written by ResizableLayout so the coachmark queues
  // immediately when the sidebar panel feature becomes available.
  const isSidebarPanelFeatureEnabled = get(sidebarPanelFeatureEnabledAtom);
  const shouldShowMenuCoachmarkTour =
    !appState.isMobile && isValidPath && isSidebarPanelFeatureEnabled;

  if (shouldShowMenuCoachmarkTour) {
    addModalToQueue({
      id: ModalId.MENU_COACHMARK_TOUR,
      priority: ModalPriority.LOW,
      dismissible: true,
      showOnce: true,
    });
  }

  // V2 scribe layout tour — LOW priority, one-time. Reads the reactive
  // eligibility atom written by SessionPageV2View so it only enqueues while
  // the wide V2 session view is actually on screen (never on mobile / compact
  // shells, where its anchors don't exist). Added after MENU_COACHMARK_TOUR so
  // the global menu tour wins when both are queued — equal LOW priority
  // resolves by insertion order (stable sort).
  const isScribeLayoutTourEligible = get(scribeLayoutTourEligibleAtom);
  if (isScribeLayoutTourEligible && isValidPath && !appState.isMobile) {
    addModalToQueue({
      id: ModalId.SCRIBE_LAYOUT_V2_TOUR,
      priority: ModalPriority.LOW,
      dismissible: true,
      showOnce: true,
    });
  }

  // V2 scribe layout invite — LOW/T4. The V1 session view publishes which
  // rollout journey is eligible. Both variants wait for an idle clinical moment
  // and suppress managed users. The Labs card persists a yes/no choice itself,
  // so it ignores `seen_modals`; the GA card records Delay/X as seen to suppress
  // only the card while the independently eligible opt-in banner remains visible.
  const scribeLayoutInviteVariant = get(scribeLayoutInviteVariantAtom);
  if (
    scribeLayoutInviteVariant &&
    isValidPath &&
    !appState.isMobile &&
    !appState.isClinicalRecordingActive &&
    !appState.isUserManaged
  ) {
    addModalToQueue({
      id: ModalId.SCRIBE_LAYOUT_V2_INVITE,
      priority: ModalPriority.LOW,
      dismissible: true,
      showOnce: scribeLayoutInviteVariant === 'ga',
    });
  }

  // Adaptive-learning opt-in popover (SCR-6378) — LOW priority, T4 education
  // surface. Reads the reactive eligibility atom written by
  // `useAdaptiveLearningPopover` while the session page is mounted: `true` only
  // when an `opt_in_ask` note-suggestions frame has arrived for the current
  // session and the user has not yet acted this session. Inserted AFTER the
  // V2 invite so the invite wins on an equal-LOW tie (both are bottom-left
  // session-page cards; the queue guarantees only the head renders, so they
  // never stack). `showOnce: false` — frequency rides on the backend policy,
  // not `seen_modals` gating here; the eligibility atom clears once the user
  // turns it on / dismisses, which drops it from the queue.
  // T4 suppressors (modal priority stack): never interrupt a RUNNING recording
  // (R1 clinical lock) and never auto-show education to managed / enterprise
  // users (R2). Deliberately `isRecording`, not `isClinicalRecordingActive`:
  // the offer fires at note-generation end, which routinely happens while the
  // consult recording sits paused — the broader paused-inclusive signal would
  // suppress the offer in its natural moment.
  const isAdaptiveLearningPopoverEligible = get(adaptiveLearningPopoverEligibleAtom);
  if (
    isAdaptiveLearningPopoverEligible &&
    !appState.isRecording &&
    !appState.isUserManaged &&
    isValidPath &&
    !appState.isMobile &&
    appState.hasToken
  ) {
    addModalToQueue({
      id: ADAPTIVE_LEARNING_OPTIN_POPOVER_MODAL_ID,
      priority: ModalPriority.LOW,
      dismissible: true,
      showOnce: false,
    });
  }

  // Heidi II Evidence tour — HIGH T4, first among educational HIGH so it
  // cannot lose a HIGH tie to Patients intro, Dictate history, or the
  // session-list tour. New educational HIGH rows must enqueue AFTER this.
  // Stays below HIGHEST (self-onboarding / T1) and CRITICAL (trial / migration).
  // One queue slot for all three spotlights; stepping is internal. Path
  // scoping is decided with the step in GlobalModalsWrapper. Suppressed on
  // mobile, for managed accounts (R2) and during a recording (R1).
  if (
    appState.showHeidi2EvidenceTourSpotlight &&
    !appState.isMobile &&
    !appState.isUserManaged &&
    isValidPath &&
    appState.hasToken &&
    appState.pathname !== '/onboarding' &&
    !appState.isClinicalRecordingActive
  ) {
    addModalToQueue({
      id: HEIDI_2_EVIDENCE_TOUR_SPOTLIGHT_MODAL_ID,
      priority: ModalPriority.HIGH,
      dismissible: true,
      showOnce: true,
    });
  }

  // Patients introduction modal — HIGH priority.
  // One-time GA intro for the Patients surface, gated on the patient-list flag.
  // Held back for accounts under 14 days old so brand-new signups aren't shown a
  // feature-intro on day one, and suppressed on the onboarding screen so it never
  // interrupts signup.
  if (
    appState.canSurfacePatientListNudges &&
    appState.isUserAtLeastTwoWeeksOld &&
    isValidPath &&
    appState.pathname !== '/onboarding' &&
    appState.hasToken
  ) {
    addModalToQueue({
      id: PATIENT_LIST_INTRODUCTION_MODAL_ID,
      priority: ModalPriority.HIGH,
      dismissible: true,
      showOnce: true,
    });
  }

  // Dictate history awareness spotlight — HIGH priority. Non-dictate signups
  // ≥14 days (held back so brand-new scribe users meet the feature organically).
  // Dictate signups get this via `enqueueModalAtom` on onboarding complete
  // instead (they land on `/dictation-logs`, which this path suppresses).
  // T4 clinical lock (R1): never interrupt an active/paused consult — pacing's
  // `isRecording` check is kill-switch gated and misses pause/Chronicle mid-
  // consult; mirror SESSION_MERGE_TAB_COACHMARK / NEW_SESSION_UPLOAD_SPOTLIGHT.
  // T4 enterprise suppress (R2): never auto-show education to managed users.
  // Only users who can see the Dictate nav entry have an anchor to host it;
  // queued without one, nothing can dismiss it and lower modals never show.
  if (
    IS_DICTATION_LOGS_ENABLED() &&
    IS_DICTATION_LOGS_VISIBLE() &&
    appState.userSignupSource !== SignupSource.dictate &&
    appState.isUserAtLeastTwoWeeksOld &&
    !appState.isClinicalRecordingActive &&
    !appState.isUserManaged &&
    isValidPath &&
    appState.pathname !== DICTATION_LOGS_ROUTE &&
    appState.hasToken
  ) {
    addModalToQueue({
      id: ModalId.DICTATION_LOGS_SPOTLIGHT,
      priority: ModalPriority.HIGH,
      dismissible: true,
      showOnce: true,
    });
  }

  // Scribe cross-sell spotlight — NORMAL. Desktop dictate users on the logs
  // page after enough dictations. Eligibility (flag / route / count / signup)
  // is computed in GlobalModalsWrapper behind `IS_DICTATION_LOGS_ENABLED`;
  // leaving `/dictation-logs` drops it from the queue without marking seen.
  // T3 enterprise suppress (R2): never auto-show conversion to managed users.
  if (
    appState.showScribeCrossSellSpotlight &&
    !appState.isUserManaged &&
    appState.hasToken
  ) {
    addModalToQueue({
      id: ModalId.SCRIBE_CROSS_SELL_SPOTLIGHT,
      priority: ModalPriority.NORMAL,
      dismissible: true,
      showOnce: true,
    });
  }

  // The Defaults settings surface can be the settings modal overlay (which keeps
  // router.pathname on the page behind it) or a full-page route, so detect both.
  const isDefaultsSettingsModalOpen =
    settingsModal.open && settingsModal.route === '/settings/defaults';
  const isDefaultsSettingsOpen =
    isDefaultsSettingsModalOpen || appState.pathname === '/settings/defaults';

  // Session Merge onboarding popover (Home) — NORMAL priority, floating awareness
  // card on the Home / session-list screen. One-time prompt to turn on auto
  // session merge: shown when the rollout flag is on and the setting is still
  // off. `/scribe` redirects to the session-list catch-all
  // (`/scribe/sessions/[[...id]]`), so match both route patterns. Suppressed
  // while the settings modal is open (the settings card covers that surface).
  if (
    appState.canPromptSessionMergeOnboarding &&
    (appState.pathname === '/scribe' ||
      appState.pathname === '/scribe/sessions/[[...id]]') &&
    !settingsModal.open &&
    appState.hasToken
  ) {
    addModalToQueue({
      id: SESSION_MERGE_ONBOARDING_MODAL_ID,
      priority: ModalPriority.NORMAL,
      dismissible: true,
      showOnce: true,
    });
  }

  // Session Merge onboarding popover (Settings) — second-chance prompt on the
  // Defaults settings surface for users who didn't enable from Home. Separate
  // modal id (separate seen-state), so it can re-prompt once even if the Home
  // card was dismissed.
  if (
    appState.canPromptSessionMergeOnboarding &&
    isDefaultsSettingsOpen &&
    appState.hasToken
  ) {
    addModalToQueue({
      id: SESSION_MERGE_ONBOARDING_SETTINGS_MODAL_ID,
      priority: ModalPriority.NORMAL,
      dismissible: true,
      showOnce: true,
    });
  }

  // Session Merge tab coachmark — LOW priority, anchored to the Session Merge
  // tab. Gated on `hasSessionMergeTab` (the open session actually renders the
  // tab), not the flag alone: the CoachMark renders null until its anchor
  // exists, and the queue shows one modal at a time with nothing to dequeue an
  // invisible one — so enqueuing it on a tab-less screen would park it at the
  // head of the LOW group and block the sibling spotlights (Patients nav,
  // file-upload, share, Verify).
  // T4 suppressors (modal priority stack): never interrupt an active recording —
  // including a *paused* one, which is still mid-consult (R1 clinical lock) —
  // and never auto-show education to managed / enterprise users (R2). The tab
  // itself stays ungated, only the coachmark is held back.
  if (
    appState.hasSessionMergeTab &&
    !appState.isClinicalRecordingActive &&
    !appState.isUserManaged &&
    isValidPath &&
    appState.hasToken
  ) {
    addModalToQueue({
      id: SESSION_MERGE_TAB_COACHMARK_MODAL_ID,
      priority: ModalPriority.LOW,
      dismissible: true,
      showOnce: true,
    });
  }

  // Patients nav spotlight — LOW priority, anchored to the Patients sidebar nav
  // item. Eligibility (feature on, 2 days after the intro modal was seen) is
  // computed in GlobalModalsWrapper.
  if (
    appState.showPatientListNavSpotlight &&
    isValidPath &&
    appState.pathname !== '/onboarding' &&
    appState.hasToken
  ) {
    addModalToQueue({
      id: PATIENT_LIST_NAV_SPOTLIGHT_MODAL_ID,
      priority: ModalPriority.LOW,
      dismissible: true,
      showOnce: true,
    });
  }

  // Session list spotlight tour — HIGH priority so it leads on the first visit
  // to the new session list, above NORMAL/LOW nudges but below self-onboarding
  // (HIGHEST) and migration / trial-end paywalls (CRITICAL). The 4-step sequence
  // shares this one queue slot; stepping is internal and never re-queues. Scoped
  // to /scribe/sessions (the only place its anchors mount) and suppressed during
  // an active clinical recording — incl. paused / Chronicle captures (priority
  // stack R1). forceShow bypasses these guards.
  // Debugger: __heidiModals.forceShow('NEW_SESSION_UPLOAD_SPOTLIGHT')
  if (
    appState.showSessionListSpotlight &&
    isValidPath &&
    appState.hasToken &&
    appState.pathname.startsWith('/scribe/sessions') &&
    isSessionListAnchorsMounted &&
    !appState.isClinicalRecordingActive
  ) {
    addModalToQueue({
      id: ModalId.NEW_SESSION_UPLOAD_SPOTLIGHT,
      priority: ModalPriority.HIGH,
      dismissible: true,
      showOnce: true,
    });
  }

  // Routines onboarding spotlight — NORMAL first-run T4. Beats
  // LOW ghost heads (file-upload / share / verify / prechart-tab) that enqueue
  // without their anchors. Stays in the T4 band. HIGH still wins; earlier-
  // enqueued NORMAL still wins; later-enqueued NORMAL ties (Prechart intro,
  // firmware) lose by insertion order — intended on first Work visit.
  // Pacing-exempt (first-run, not a recurring tip).
  // One queue slot for the 4-step tour; stepping is internal. Scoped to
  // /scribe/chat/projects (the only place its sidebar + browse anchors mount)
  // and suppressed on mobile, managed/enterprise (R2 — tier framework is
  // default-off), and during an active clinical recording (priority stack R1).
  // Debugger: __heidiModals.forceShow('T4_CUSTOM_AUDIENCE_ROUTINES_ONBOARDING_SPOTLIGHT')
  if (
    appState.showRoutinesSpotlight &&
    !appState.isMobile &&
    !appState.isUserManaged &&
    isValidPath &&
    appState.hasToken &&
    appState.pathname.startsWith('/scribe/chat/projects') &&
    isRoutinesSpotlightSidebarAnchorsMounted &&
    !appState.isClinicalRecordingActive
  ) {
    addModalToQueue({
      id: ModalId.T4_CUSTOM_AUDIENCE_ROUTINES_ONBOARDING_SPOTLIGHT,
      priority: ModalPriority.NORMAL,
      dismissible: true,
      showOnce: true,
    });
  }

  // Heidi II confirm-access — NORMAL T4, once per team admin on Heidi II. Asks
  // whether everyone or chosen individuals get Heidi II. Audience (admin, Heidi
  // II, team access controls flag) is computed in GlobalModalsWrapper; device,
  // managed-account (R2) and recording (R1) guards live here.
  if (
    appState.showHeidi2ConfirmAccessModal &&
    !appState.isMobile &&
    !appState.isUserManaged &&
    isValidPath &&
    appState.hasToken &&
    appState.pathname !== '/onboarding' &&
    !appState.isClinicalRecordingActive
  ) {
    addModalToQueue({
      id: HEIDI_2_CONFIRM_ACCESS_MODAL_ID,
      priority: ModalPriority.NORMAL,
      dismissible: true,
      showOnce: true,
    });
  }

  // Automatically pull in history — LOW priority, one-time opt-in for linked-session context.
  // Eligibility (patient + prior sessions) is synced from PastSessionsButton via
  // autoPullHistorySpotlightEligibleAtom. Debugger: __heidiModals.forceShow('INTENDS_TO_AUTO_LINK_SESSIONS')
  if (
    appState.isAutoLinkSessionsEnabled &&
    isAutoPullHistorySpotlightEligible &&
    isValidPath &&
    appState.hasToken
  ) {
    addModalToQueue({
      id: ModalId.INTENDS_TO_AUTO_LINK_SESSIONS,
      priority: ModalPriority.LOW,
      dismissible: true,
      showOnce: true,
    });
  }

  // Share session announce spotlight — LOW priority, image-based launch
  // coachmark introducing direct session sharing. Anchored to the share
  // button. Audience targeting (e.g. active power users) is handled in
  // Flagsmith via identity traits on
  // `release-scribe-session-share-announce-spotlight`.
  if (
    appState.isShareSessionAnnounceSpotlightEnabled &&
    appState.isLinkShareAvailable &&
    isValidPath &&
    appState.hasToken
  ) {
    addModalToQueue({
      id: ModalId.SHARE_SESSION_ANNOUNCE_SPOTLIGHT,
      priority: ModalPriority.LOW,
      dismissible: true,
      showOnce: true,
    });
  }

  // Heidi Verify spotlight — LOW priority, shows once to users with the
  // entity-verification flag. The "after note generation finishes" timing is
  // enforced inside the spotlight component by anchoring to the Verify button,
  // which only exists in the DOM post-generation.
  // Held back for accounts under 14 days old so new signups finish Scribe
  // onboarding before meeting the Verify spotlight (SCR-5551).
  if (
    IS_ENTITY_VERIFICATION_ENABLED() &&
    isValidPath &&
    appState.hasToken &&
    appState.isUserAtLeastTwoWeeksOld
  ) {
    addModalToQueue({
      id: ModalId.HEIDI_VERIFY_SPOTLIGHT,
      priority: ModalPriority.LOW,
      dismissible: true,
      showOnce: true,
    });
  }

  // Precharting beta (Athena / Google ADK agents) introduction — NORMAL
  // priority. One-time intro for users on the `release-prechart` flag.
  // Suppressed on the onboarding screen so it never interrupts signup (mirrors
  // the patient-list / affiliate-link intros).
  // Gated on org/region context (not just hasToken) so it waits for the app to
  // load — hasToken resolves at boot, before the UI renders, which would
  // otherwise pop the modal over a blank loading screen. Pair hasOrganisationInfo
  // with isNonAdminTeamMember (as elsewhere in this file): non-admin team members
  // never own org info, so a strict hasOrganisationInfo check would permanently
  // suppress the intro for them rather than just delaying it.
  if (
    IS_PRECHART_ENABLED() &&
    isValidPath &&
    appState.pathname !== '/onboarding' &&
    appState.hasToken &&
    (appState.hasOrganisationInfo || appState.isNonAdminTeamMember) &&
    appState.hasRegion
  ) {
    addModalToQueue({
      id: PRECHART_BETA_INTRO_MODAL_ID,
      priority: ModalPriority.NORMAL,
      dismissible: true,
      showOnce: true,
    });

    // Companion prechart-tab spotlight — LOW priority, and only after the intro
    // modal has been seen, so the modal always comes first. The "in a session"
    // timing is enforced inside the spotlight component by anchoring to the
    // Prechart tab, which only exists in the DOM once a prechart document does.
    if (seenModalIds.has(PRECHART_BETA_INTRO_MODAL_ID)) {
      addModalToQueue({
        id: PRECHART_BETA_TAB_SPOTLIGHT_ID,
        priority: ModalPriority.LOW,
        dismissible: true,
        showOnce: true,
      });
    }
  }

  // Chronicle firmware startup modal — NORMAL priority, Tauri desktop only.
  // Per-session (firmwareStartupSeenAtom) rather than showOnce — no backend
  // persistence needed. The seen atom resets on sign-out so the modal
  // re-triggers each session. (The id's local-regime status and the cast that
  // retires it are documented once, at FIRMWARE_MODAL_ID.)
  if (
    appState.isTauri &&
    appState.isFirmwareStartupCheckEnabled &&
    appState.firmwareUpdateAvailable &&
    appState.firmwareUpdateStatus === null &&
    !appState.isRecording &&
    !firmwareStartupSeen
  ) {
    addModalToQueue({
      id: FIRMWARE_MODAL_ID,
      priority: ModalPriority.NORMAL,
      dismissible: true,
    });
  }

  if (
    appState.isTauri &&
    appState.userSignupSource === SignupSource.dictate &&
    appState.isDictationEnabled &&
    appState.hasToken &&
    appState.clinicianTrialAvailable &&
    appState.pathname === '/dictation-logs'
  ) {
    addModalToQueue({
      id: ModalId.CLINICIAN_TRIAL,
      priority: ModalPriority.NORMAL,
      dismissible: true,
      showOnce: true,
    });
  }

  // Mobile has no popover. Queueing here would jam the launch queue, and
  // force-closing it would consume the cycle threshold plus a pacing slot
  // before anyone sees the nudge. Desktop still launches it.
  if (
    isValidPath &&
    appState.hasToken &&
    !appState.isMobile &&
    appState.shouldQueueSideNavCreditUsageThresholdPopover
  ) {
    addModalToQueue(SIDE_NAV_CREDIT_USAGE_POPOVER_QUEUE_ITEM);
  }

  /*
   * SECOND SOURCE — package `./contributions` (OpenSpec monorepo-shell-registry
   * task S2). Registry entries join here, at the launch-candidate stage, and
   * they go through the SAME `addModalToQueue` as every legacy entry above, so
   * `showOnce`/`seenModalIds`, `showOnlyAfterAccountCreation` and the
   * null-guard early-return apply identically. Everything downstream — the
   * tier gate, pacing, the user-triggered merge, the priority sort — therefore
   * cannot tell which source a modal came from, which is the property that
   * makes a per-domain flip behavior-invisible.
   *
   * Appended AFTER the legacy chain on purpose: push order is the stable
   * sort's tie-break, so appending cannot disturb the existing effective order
   * (design D5's freeze). This must stay the LAST thing the atom does, and
   * `src/atoms/modal-queue-first-flip.test.ts` asserts it from source, because
   * every ordering claim a flipped entry makes rests on it.
   *
   * EVERY ROW ABOVE MUST NAME A SYMBOLIC `ModalPriority` LEVEL. A contributed
   * entry may sit BETWEEN two levels — one does today, at `2` — so a bare
   * integer here can land in a gap and silently interleave with it, which push
   * order cannot arbitrate because the two are in different sources. Naming a
   * level makes that unrepresentable; `modal-queue-growth-csat-flip.test.ts`
   * pins it from this file's source text, alongside a by-name equality over the
   * whole `LOW` band.
   *
   * The registry is no longer empty. The first flipped entry is
   * `@heidi/dictate`'s `newInThisReleaseCard` — the dictation "New in this
   * release" card, whose declaration used to sit at the end of the chain above.
   * It was the last entry pushed, so moving it here leaves the array it
   * produces identical rather than merely equivalent; that suite pins the
   * equality, and `launchModalsConfigAtom.test.ts` still exercises the card's
   * whole gate through this atom without knowing which source it came from.
   */
  for (const contribution of get(appRegistryAtom).modals) {
    if (!contribution.eligible(appState)) continue;
    addModalToQueue({
      // The legacy `QueuedModal.id` predates the dual-id regime, so a
      // local-regime id needs the same cast the app already uses for
      // KINDE_MIGRATION_MODAL_ID / FIRMWARE_MODAL_ID. It retires with the
      // legacy type at the last flip (task Z3), not before.
      id: contribution.id as ModalId,
      priority: contribution.priority,
      dismissible: contribution.dismissible,
      showOnce: contribution.showOnce,
      showOnlyAfterAccountCreation: contribution.showOnlyAfterAccountCreation,
    });
  }

  return modals;
});
